import React from 'react';
import { Users, Award, Globe, ArrowRight, Lightbulb, HandshakeIcon, Shield, Target, Palette, Wrench, Heart } from 'lucide-react';
import HeroImage from '../components/HeroImage';
import AnimatedSection from '../components/AnimatedSection';

function About() {
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1920&h=1080" 
              alt="Technology background"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-5xl font-bold text-center text-white mb-6">About Atlas AI Business Solutions</h1>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              Who We Are, Where We Came From, Where We're Going
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Sections */}
      <div className="max-w-7xl mx-auto px-4">
        {/* Vision Section */}
        <section className="py-20">
          <AnimatedSection>
            <div className="white-holographic-card p-12 rounded-2xl text-center">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Our Vision</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                To be a trusted partner for businesses worldwide, large and small, through solving problems with the aid of Artificial Intelligence at human direction and control.
              </p>
            </div>
          </AnimatedSection>
        </section>

        {/* Introduction Section */}
        <section className="py-20">
          <AnimatedSection>
            <div className="white-holographic-card p-12 rounded-2xl">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Who We Are</h2>
              <div className="prose prose-lg text-gray-600">
                <p className="mb-6">
                  Founded by a team of forward thinking industry veterans dedicated to producing solutions in the hardest to reach places, we came together through the use of AI ourselves! From the content creation space came 2 former music video directors and photographers, well versed in the art of substance and spectacle. From the internet space came our team's website developers and social media wizards. And last but not least, were our service industry veterans from hospitality and human resources, experts in creating customer friendly spaces, tools and products.
                </p>
                <p>
                  We are a forward-thinking agency dedicated to transforming the way businesses operate through cutting-edge artificial intelligence solutions. Our mission is to empower businesses with the tools they need to thrive in a competitive, ever-evolving market.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* Values Section */}
        <section className="py-20">
          <h2 className="text-3xl font-bold text-white mb-12">Our Core Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <AnimatedSection>
              <div className="white-holographic-card p-8 rounded-xl">
                <div className="mb-6">
                  <Lightbulb className="h-12 w-12 text-[#00ffff]" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">Innovation</h3>
                <p className="text-gray-600">
                  We embrace the latest advancements in AI to craft solutions that keep our clients ahead of the curve.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection>
              <div className="white-holographic-card p-8 rounded-xl">
                <div className="mb-6">
                  <HandshakeIcon className="h-12 w-12 text-[#ff0000]" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">Collaboration</h3>
                <p className="text-gray-600">
                  We believe in fostering strong partnerships with our clients, working hand-in-hand to achieve their goals.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection>
              <div className="white-holographic-card p-8 rounded-xl">
                <div className="mb-6">
                  <Award className="h-12 w-12 text-[#00ffff]" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">Excellence</h3>
                <p className="text-gray-600">
                  Our commitment to quality ensures that every solution we deliver meets the highest standards.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection>
              <div className="white-holographic-card p-8 rounded-xl">
                <div className="mb-6">
                  <Shield className="h-12 w-12 text-[#ff0000]" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">Integrity</h3>
                <p className="text-gray-600">
                  Trust and transparency are the foundation of our business relationships.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12">What We Do</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <AnimatedSection>
                <div className="white-holographic-card p-8 rounded-xl">
                  <div className="mb-6">
                    <Target className="h-12 w-12 text-[#00ffff]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-800 mb-4">Lead Generation</h3>
                  <p className="text-gray-600">
                    Using AI to identify and capture high-quality leads, driving revenue and growth for your business.
                  </p>
                </div>
              </AnimatedSection>
              <AnimatedSection>
                <div className="white-holographic-card p-8 rounded-xl">
                  <div className="mb-6">
                    <Palette className="h-12 w-12 text-[#ff0000]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-800 mb-4">Website Creation</h3>
                  <p className="text-gray-600">
                    Developing AI-powered websites that are not only visually stunning but also optimized for user engagement and performance.
                  </p>
                </div>
              </AnimatedSection>
              <AnimatedSection>
                <div className="white-holographic-card p-8 rounded-xl">
                  <div className="mb-6">
                    <Wrench className="h-12 w-12 text-[#00ffff]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-800 mb-4">Custom Solutions</h3>
                  <p className="text-gray-600">
                    Building tailored AI tools and systems to meet the specific needs of your business.
                  </p>
                </div>
              </AnimatedSection>
              <AnimatedSection>
                <div className="white-holographic-card p-8 rounded-xl">
                  <div className="mb-6">
                    <Heart className="h-12 w-12 text-[#ff0000]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-800 mb-4">Customer Retention</h3>
                  <p className="text-gray-600">
                    Leveraging AI analytics to improve customer engagement, satisfaction, and loyalty.
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20">
          <AnimatedSection>
            <div className="white-holographic-card p-12 rounded-2xl">
              <h2 className="text-3xl font-bold text-gray-800 mb-8">Why Choose Us?</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Expertise</h3>
                  <p className="text-gray-600 mb-6">
                    Our team consists of industry-leading AI professionals and business strategists with years of experience.
                  </p>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Tailored Solutions</h3>
                  <p className="text-gray-600">
                    We understand that no two businesses are the same. That's why we create personalized solutions to fit your unique needs.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Proven Results</h3>
                  <p className="text-gray-600 mb-6">
                    We've helped businesses both large and small achieve measurable success, from increased revenue to improved customer satisfaction.
                  </p>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Continuous Support</h3>
                  <p className="text-gray-600">
                    Our partnership doesn't end at deployment. We provide ongoing support and optimization to ensure lasting impact.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </section>
      </div>
    </div>
  );
}

export default About;